import {
	type ExcelNotification,
	type FormSummaryJSONObject,
	IkeToken,
	IkeUserRole,
	type ImportResult,
	isImportTaskResult,
	type JobResult,
} from '@ikegps/core-api';
import { IkeCSV, IkeRequestError } from '@ikegps/core-shared';
import { action, computed, observable, makeObservable } from 'mobx';
import { ImageButton } from '../../components/button/image.button';
import {
	EXCEL_MIME_TYPE,
	FileUploadComponent,
	OPEN_EXCEL_MIME_TYPE,
	ZIP_MIME_TYPE,
	WINDOWS_ZIP_MIME_TYPE,
} from '../../components/file.upload/file.upload';
import { Icon } from '../../components/icon';
import { MaterialIcon } from '../../components/icon/icons';
import { SelectInputComponent, SelectInputState } from '../../components/input/input.select';
import { TagNameInputState } from '../../components/input/tag.input.state';
import { ModalFormComponent } from '../../components/modal.form/modal.form.component';
import { ModalService } from '../../components/modal/modal.service';
import { CSS as ModalCSS } from '../../components/modal/modal.style';
import { NotificationService } from '../../components/notification.toast/notification.service';
import { Spinner } from '../../components/spinner/spinner';
import { DefaultTheme } from '../../components/theme';
import { T, Translate } from '../../i18n';
import { type CustomMithrilVNode, m, type MithrilVNode } from '../../mithril';
import { MessageReasonTranslations } from '../../modules/export.notification/export.notification.detail/export.notification.detail.ctrl';
import { getLongRunningTaskManager } from '../../modules/long.running.task.manager';
import { Services } from '../../services';
import { AppStore } from '../../store';
import { classNames } from '../../util/classnames';
import JSZip from 'jszip';
import { Accordion } from '../../components/accordion/accordion';
import { IkePage, type IkePageConfig, IkePageName } from '../page';
import { CSS as ImportCtrlCSS, CSVFormImportControl } from './import.ctrl';
import { CSS } from './import.style';

const MAX_COLLECTIONS = 500;
const MAX_EXCEL_FILES = 50;
const COLLECTION_CHUNK = 25;

interface Tab {
	title: string;
	/**
	 * Optional callback when selected
	 */
	onSelect?: () => void;
}

export const CSVFileImportPageConfig: IkePageConfig = {
	type: 'internal',
	name: IkePageName.collectionImport,
	path: '/collection/import',
	auth: {
		required: true,
		tokens: [IkeToken.FEATURE_IMPORT_CSV],
		userLevel: [IkeUserRole.USER, IkeUserRole.ROOT, IkeUserRole.DATA_ADMIN],
	},
	nav: {
		name: T.LIST_COLLECTION_IMPORT,
		icon: MaterialIcon.INBOX,
	},
};

class ImportingModal extends ModalFormComponent {
	constructor() {
		super({
			title: T.IMPORTING_COLLECTIONS,
			confirm: T.OK,
			success: { key: T.COLLECTION_IMPORT_SUCCESS },
		});
		this.displaySubmitButton = false;
		this.displayCancelButton = false;
	}

	override canClose() {
		return false;
	}

	get nodes(): MithrilVNode {
		return m('div', [
			Translate(T.IMPORTING_MODAL_DESCRIPTION),
			m('div', { className: ImportCtrlCSS.spinner }, Spinner.view()),
		]);
	}
}

export class CSVFileImportPage extends IkePage {
	tag: TagNameInputState;
	form = new SelectInputState('Select a Form');
	fileUpload = new FileUploadComponent();

	csvImport: CSVFormImportControl = new CSVFormImportControl();
	importingModal = new ImportingModal();

	@observable isImporting = false;
	@observable progressCurrent = 0;
	@observable progressTotal = 0;

	@observable mode: 'EXCEL' | 'CSV' | null = null; // Initial value is set in constructor

	private tabs: Tab[] = [];
	private activeTab = 0;

	get canImportCSV() {
		return this.isCorrectUserType && Services.Token.has(IkeToken.FEATURE_IMPORT_CSV);
	}

	get canImportExcel() {
		return this.isCorrectUserType && Services.Token.has(IkeToken.FEATURE_IMPORT_EXCEL);
	}

	get canImport() {
		return this.canImportCSV || this.canImportExcel;
	}

	get isExcelImport() {
		return this.mode === 'EXCEL';
	}

	get isCSVImport() {
		return this.mode === 'CSV';
	}

	get isCorrectUserType() {
		const userProfile = Services.UserProfile.get(Services.User.current.objectId);
		return Services.UserProfile.canImportCollections(userProfile);
	}

	@computed get isValid() {
		const fileType = this.fileUpload.file?.type;
		if (this.tagName == null && fileType !== ZIP_MIME_TYPE && fileType !== WINDOWS_ZIP_MIME_TYPE) {
			return false;
		}

		if (this.isExcelImport) {
			// Require the file's content to be read (FileReader is async), not just its type — the import
			// payload is file.text, so the button must not enable before the read completes. See UFO-743.
			return Boolean(
				fileType &&
				[EXCEL_MIME_TYPE, OPEN_EXCEL_MIME_TYPE, ZIP_MIME_TYPE, WINDOWS_ZIP_MIME_TYPE].includes(fileType) &&
				this.fileUpload.file?.text
			);
		}

		if (this.isCSVImport) {
			if (!this.csvImport.isValid) {
				return false;
			}
			if (this.csvImport.collections.length > MAX_COLLECTIONS || this.csvImport.collections.length < 1) {
				return false;
			}
		}

		return true;
	}

	constructor() {
		super(CSVFileImportPageConfig);
		makeObservable(this);

		this.tag = new TagNameInputState();
		this.tag.state.setAutoFocus(true);

		this.reaction(
			() => this.mode,
			() => {
				if (this.mode === 'EXCEL') {
					this.fileUpload.uploadType = 'buffer';
				}
				if (this.mode === 'CSV') {
					this.fileUpload.uploadType = 'text';
				}
			}
		);
		this.reaction(() => this.fileUpload.file && this.fileUpload.file.text, this.clearJobNameIfZip);
		this.reaction(() => this.fileUpload.file && this.fileUpload.file.text, this.processCSVFile);
		this.reaction(() => this.fileUpload.file && this.fileUpload.file.isFailed, this.processFileFailure);
		this.reaction(() => this.form.value, this.processCSVFile);
		this.reaction(() => AppStore.FormSummary.all, this.processFormList);

		// Select initial mode (active tab) based on permissions
		if (this.canImportExcel) {
			this.mode = 'EXCEL';
			this.fileUpload.uploadType = 'buffer';
		} else if (this.canImportCSV) {
			this.mode = 'CSV';
			this.fileUpload.uploadType = 'text';
		}

		// If we're navigating back to this page, and the FormSummaries have already been loaded in the app store
		// the above reaction will never be triggered and the form list will never be populated. This
		// manually calls the processFormList method to ensure it's populated regardless.
		this.processFormList(AppStore.FormSummary.all);

		this.tabs = [
			...(this.canImportExcel ? [{ title: Translate(T.EXCEL), onSelect: () => (this.mode = 'EXCEL') }] : []),
			...(this.canImportCSV ? [{ title: Translate(T.CSV), onSelect: () => (this.mode = 'CSV') }] : []),
		];

		this.isImporting = false;
	}

	get tagName() {
		const tagValue = this.tag.value;
		if (tagValue == null || tagValue.trim() === '') {
			return null;
		}
		return tagValue;
	}

	@action.bound clearJobNameIfZip() {
		if (this.fileUpload.file?.type === ZIP_MIME_TYPE || this.fileUpload.file?.type === WINDOWS_ZIP_MIME_TYPE) {
			this.tag.clearValue();
			(document.activeElement as HTMLElement)?.blur();
		}
	}

	@action.bound processCSVFile() {
		// Only run if CSV import is selected
		if (!this.isCSVImport) {
			return;
		}

		if (this.form.value == null) {
			this.csvImport.reset();
			return;
		}

		const file = this.fileUpload.file;
		if (file == null) {
			this.csvImport.reset();
			return;
		}

		const text = this.fileUpload.file.text;
		if (text == null) {
			this.csvImport.reset();
			return;
		}

		try {
			this.csvImport.process(this.form.value, new IkeCSV(text));
		} catch (_e) {
			NotificationService.error({ key: T.FILE_NOT_SUPPORTED });
			this.fileUpload.reset();
			this.csvImport.reset();
		}
	}
	@action setProgress(current: number, total: number) {
		this.progressCurrent = current;
		this.progressTotal = total;
	}

	@action.bound processFileFailure(failed: boolean) {
		if (!failed) {
			return;
		}

		this.setImporting(false);
		this.fileUpload.reset();
		NotificationService.error({ key: T.FILE_NOT_SUPPORTED });
	}

	@action setImporting(importing: boolean) {
		this.isImporting = importing;
		this.setProgress(0, 0);
	}

	@action.bound async importCSVCollections() {
		if (this.isImporting) {
			return;
		}

		this.setProgress(0, this.csvImport.collections.length);
		this.isImporting = true;

		try {
			await this.processCSVImport();
			this.setImporting(false);
		} catch (e: unknown) {
			NotificationService.error({ key: T.COLLECTION_IMPORT_FAILED });
			Services.Log.Logger.error(e, 'Error importing collections');
		}
	}

	@action.bound async importExcelCollections() {
		if (this.isImporting) {
			return;
		}
		this.setImporting(true);

		try {
			const file = this.fileUpload.file;

			if (!file || !file.text) {
				NotificationService.error({ key: T.FILE_NOT_SUPPORTED });
				this.setImporting(false);
				return;
			}

			// Client-side ZIP validation — count Excel files and collect skipped names before uploading
			let skippedFiles: string[] = [];
			if (this.fileUpload.file?.type === ZIP_MIME_TYPE || this.fileUpload.file?.type === WINDOWS_ZIP_MIME_TYPE) {
				const zip = await JSZip.loadAsync(file.text);
				const nonHiddenFiles = Object.entries(zip.files).filter(([name, entry]) => {
					if (entry.dir) return false;
					const parts = name.split('/');
					return !parts.some((part) => part.startsWith('.'));
				});
				const xlsxFiles = nonHiddenFiles.filter(([name]) => name.endsWith('.xlsx'));
				skippedFiles = nonHiddenFiles
					.filter(([name]) => !name.endsWith('.xlsx'))
					.map(([name]) => name.replace(`${file.name.replace('.zip', '')}/`, ''));

				Services.Log.Logger.info(
					{ count: xlsxFiles.length, files: xlsxFiles.map(([name]) => name) },
					'Excel files found in zip'
				);
				if (xlsxFiles.length > MAX_EXCEL_FILES) {
					NotificationService.error({
						key: T.COLLECTION_IMPORT_TOO_MANY_FILES,
						attrs: { max: MAX_EXCEL_FILES },
					});
					this.setImporting(false);
					return;
				}
				if (xlsxFiles.length < 1) {
					NotificationService.error({
						key: T.COLLECTION_IMPORT_NO_EXCEL_FILES,
					});
					this.setImporting(false);
					return;
				}
			}

			// Upload the file to get an attachment ID, then start an async import task
			const rest = Services.Department.rest;
			const attachment = await rest.Attachment.set(file.name, file.text, file.type);

			const taskManager = getLongRunningTaskManager();
			const taskId = await taskManager.service.startImportTask({
				attachmentId: attachment.objectId,
				tagName: this.tagName,
				...(this.form.value != null ? { formRevision: this.form.value } : {}),
			});

			this.importingModal.open();

			// Block until the background task reaches a terminal state
			const completedTask = await taskManager.service.awaitTaskCompletion(taskId);

			this.importingModal.close();
			this.setImporting(false);

			if (completedTask.status === 'failed') {
				NotificationService.error({ key: T.COLLECTION_IMPORT_FAILED });
				return;
			}

			const jobResults =
				completedTask.result != null && isImportTaskResult(completedTask.result) ? completedTask.result.jobResults : [];

			let modalAction: CustomMithrilVNode<any> | null = null;
			const allSuccess =
				jobResults.length > 0 &&
				jobResults.every(
					(job) =>
						job.results.meta.length === 0 &&
						job.results.collections.every(
							(c) => (c.status === 'created' || c.status === 'updated') && c.notifications.length === 0
						)
				);
			if (allSuccess) {
				const firstSuccessfulCollection = jobResults[0]?.results.collections[0];
				const collectionUUID = firstSuccessfulCollection?.collection?.uuid ?? null;
				const jobId = firstSuccessfulCollection?.collection?.jobuuid;

				if (collectionUUID && jobId) {
					const redirectToJob = () => {
						ModalService.close();
						Services.Route.go(IkePageName.map, {
							t: jobId,
							collection: collectionUUID,
						});
					};
					modalAction = m(
						'button',
						{
							className: `mdl-button ${ModalCSS.action.ok}`,
							onclick: redirectToJob,
						},
						Translate(T.OK)
					);
				}
			}

			ModalService.open({
				title: T.COLLECTION_IMPORT_NOTIFICATIONS,
				content: () => renderAllImportResults(jobResults, skippedFiles),
				actions: modalAction,
			});
		} catch (e: unknown) {
			this.setImporting(false);
			this.importingModal.close();
			NotificationService.error({ key: T.COLLECTION_IMPORT_FAILED });
			Services.Log.Logger.error(e, 'Error importing collections');
		}
	}

	@action.bound processFormList(summaries: FormSummaryJSONObject[]) {
		this.form.setOptions(
			summaries.map((c) => {
				return { value: c.revision, key: c.displayname };
			})
		);
	}

	async processCSVImport() {
		const collections = this.csvImport.collections;

		const file = this.fileUpload.file;
		const rest = Services.Department.rest;
		const attachment = await rest.Attachment.set(file.name, file.text, file.type);

		const mapping = this.csvImport.mapper.toJSON();
		const importArgs = {
			attachmentId: attachment.objectId,
			tagName: this.tag.value,
			formId: this.csvImport.form.id,
			mapping,
			count: COLLECTION_CHUNK,
			skip: 0,
		};

		for (const info of mapping) {
			Services.Analytics.trackEvent(
				Services.Analytics.EventCategory.Import,
				'csv:mapping',
				info.fieldId + ':' + info.csvHeader,
				1
			);
		}

		let response;
		for (let i = 0; i < collections.length; i += COLLECTION_CHUNK) {
			importArgs.skip = i;
			response = await rest.Collection.Import.csv(importArgs);
			this.setProgress(i, collections.length);
		}
		NotificationService.success({
			key: T.COLLECTION_IMPORT_SUCCESS,
			attrs: { count: collections.length },
		});

		Services.Analytics.trackEvent(
			Services.Analytics.EventCategory.Import,
			'csv:collection',
			attachment.objectId,
			response.results.collections.length
		);
		Services.Route.go(IkePageName.map, {
			t: response.results.jobId,
			collection: response.results.collections[0].uuid,
		});
		this.setProgress(null, null);
	}

	private getCsvNodes() {
		if (!this.canImportCSV) {
			return null;
		}

		const children: MithrilVNode[] = [];

		children.push(this.viewJobSelector());
		children.push(this.viewFormSelector());
		children.push(this.viewFileUpload());

		if (this.fileUpload.isFilePresent === false || this.form.value == null) {
			return children;
		}

		if (this.isImporting) {
			children.push(
				m('div', { className: CSS.option.container + ' ' + CSS.option.importing }, [
					m('div', { className: CSS.option.textContainer }, [
						Spinner.view(),
						m(
							'span',
							{ className: CSS.option.text },
							Translate(T.COLLECTION_IMPORT_PROGRESS, {
								count: this.progressCurrent,
								total: this.progressTotal,
							})
						),
					]),
				])
			);
			return children;
		}

		children.push(this.viewFileMappings());
		children.push(
			m('div', { className: CSS.importButton }, [
				m(
					'button',
					{
						className: 'mdl-button mdl-button--raised mdl-button--accent',
						disabled: !this.isValid,
						onclick: this.importCSVCollections,
					},
					[Translate(T.COLLECTION_IMPORT_GO)]
				),
			])
		);

		return children;
	}

	private getExcelNodes() {
		if (!this.canImportExcel) {
			return null;
		}

		const children: MithrilVNode[] = [];

		children.push(this.viewUserGuideLink());
		children.push(this.viewFormSelector());
		children.push(this.viewExcelTemplateDownload());
		children.push(m('hr'));
		children.push(this.viewFileUpload());
		children.push(this.viewJobSelector());

		children.push(
			m('div', { className: CSS.importButton }, [
				m(
					'button',
					{
						className: 'mdl-button mdl-button--raised mdl-button--accent',
						disabled: !this.isValid || this.isImporting,
						onclick: this.importExcelCollections,
					},
					[this.isImporting ? Spinner.view() : Translate(T.COLLECTION_IMPORT_GO)]
				),
			])
		);

		return children;
	}

	get nodes() {
		const nodes = m('div', { className: CSS.container }, [
			m('div', { className: CSS.title }, m('h6', Translate(T.COLLECTION_IMPORT_TITLE))),
		]);

		// If the user is not the correct user type OR they don't have access to this feature, then block
		if (!this.canImport) {
			nodes.children.push(m('p', { className: CSS.title }, Translate(T.COLLECTION_IMPORT_NOT_ALLOWED)));
			return nodes;
		}

		if (this.tabs.length > 0) {
			nodes.children.push(
				m(
					'div',
					{ class: CSS.tabBar.container },
					this.tabs.map((tab, i) => {
						return m(
							'div',
							{
								class: classNames(CSS.tabBar.tab, {
									[CSS.tabBar.tabActive]: this.activeTab === i,
								}),
								onclick: () => {
									this.activeTab = i;
									this.tabs[i].onSelect?.();
								},
							},
							tab.title
						);
					})
				)
			);
		}

		switch (this.mode) {
			case 'EXCEL':
				nodes.children.push(...this.getExcelNodes());
				break;
			case 'CSV':
				nodes.children.push(...this.getCsvNodes());
				break;
			default:
		}

		return nodes;
	}

	viewTickOrNeeded(isComplete: boolean) {
		if (isComplete) {
			return Icon.view(MaterialIcon.CHECK, {
				className: CSS.isOptionIconComplete,
			});
		}
		return Icon.view(MaterialIcon.KEY_RIGHT);
	}

	viewJobSelector() {
		const isComplete = this.tagName != null;
		let className = CSS.job.container + ' ' + CSS.option.container;
		if (isComplete) {
			className += ' ' + CSS.isOptionComplete;
		}
		if (this.fileUpload.file?.type === ZIP_MIME_TYPE || this.fileUpload.file?.type === WINDOWS_ZIP_MIME_TYPE) {
			className += ' ' + CSS.option.disabled;
		}
		return m('div', { className }, [
			m('div', { className: CSS.option.textContainer, style: { display: 'block' } }, [
				m('div', { style: { display: 'flex' } }, [
					this.viewTickOrNeeded(isComplete),
					m('span', { className: CSS.option.text }, Translate(T.COLLECTION_IMPORT_CHOOSE_JOB)),
				]),
				m('span', { className: CSS.option.subText }, Translate(T.COLLECTION_IMPORT_CHOOSE_JOB_DESCRIPTION)),
			]),
			m('div', { className: CSS.option.value }, [this.tag.view()]),
		]);
	}

	viewExcelTemplateDownload() {
		return m(
			'div',
			{
				className: classNames(CSS.option.container, {
					[CSS.option.disabled]: !this.form.value,
				}),
			},
			[
				m('div', { className: CSS.option.textContainer }, [
					m('span', { className: CSS.option.text }, Translate(T.COLLECTION_IMPORT_EXCEL_TEMPLATE)),
				]),
				m('div', { className: CSS.option.value }, [
					m(
						'button',
						{
							className: CSS.option.download,
							onclick: async () => {
								if (this.form.value == null) {
									return;
								}
								try {
									const result = await Services.Department.rest.Collection.exportImportableExcelTemplate({
										formVersion: this.form.value,
									});
									if (result == null) {
										return;
									}
									if (result.error) {
										NotificationService.error({
											key: MessageReasonTranslations[result.error.reason],
										});
									} else if (result.url != null) {
										Services.Download.download(result.url, result.fileName);
									}
								} catch (err) {
									if (IkeRequestError.isIkeRequestError(err)) {
										await ModalService.open({
											title: Translate(T.NOTIFICATION_ERROR_TITLE),
											content: err.statusMessage,
										});
									}
								}
							},
						},
						[Icon.view(MaterialIcon.FILE_DOWNLOAD), Translate(T.EXCEL)]
					),
				]),
			]
		);
	}

	viewUserGuideLink() {
		return m('div', { className: CSS.option.container }, [
			m('div', { className: CSS.option.textContainer }, [
				Translate(T.COLLECTION_IMPORT_EXCEL_USER_GUIDE),
				m(
					'a',
					{
						href: Translate(T.COLLECTION_IMPORT_EXCEL_USER_GUIDE_LINK),
						target: '_blank',
						style: `margin-left: ${DefaultTheme.space(1)}`,
					},
					Translate(T.HERE)
				),
			]),
		]);
	}

	viewFormSelector() {
		const isComplete = this.form.value != null;
		let className = CSS.form.container + ' ' + CSS.option.container;
		if (isComplete) {
			className += ' ' + CSS.isOptionComplete;
		}

		return m('div', { className }, [
			m('div', { className: CSS.option.textContainer }, [
				this.viewTickOrNeeded(isComplete),
				m('span', { className: CSS.option.text }, Translate(T.COLLECTION_IMPORT_CHOOSE_FORM)),
			]),
			m('div', { className: CSS.option.value }, [SelectInputComponent.view(this.form)]),
		]);
	}

	viewFileUpload() {
		const isComplete = this.fileUpload.isFilePresent;
		let className = CSS.file.container + ' ' + CSS.option.container;
		if (isComplete) {
			className += ' ' + CSS.isOptionComplete;
			return m('div', { className }, [
				m('div', { className: CSS.option.textContainer }, [
					this.viewTickOrNeeded(isComplete),
					m(
						'span',
						{ className: CSS.option.text },
						this.mode === 'EXCEL' ? Translate(T.COLLECTION_IMPORT_FILE_EXCEL) : Translate(T.COLLECTION_IMPORT_FILE_CSV)
					),
				]),
				m('div', { className: CSS.option.value }, [
					m('div', this.fileUpload.file.name),
					this.isImporting
						? null
						: ImageButton.view(MaterialIcon.CLOSE, {
								onclick: this.fileUpload.reset,
							}),
				]),
			]);
		}

		return m('div', { className }, [
			m('div', { className: CSS.option.textContainer, style: { display: 'block' } }, [
				m('div', { style: { display: 'flex' } }, [
					this.viewTickOrNeeded(isComplete),
					m(
						'span',
						{ className: CSS.option.text },
						Translate(T.COLLECTION_IMPORT_CHOOSE_FILE_TYPE, Translate(this.mode === 'EXCEL' ? T.EXCEL : T.CSV))
					),
				]),
				this.mode === 'EXCEL'
					? m('span', { className: CSS.option.subText }, Translate(T.COLLECTION_IMPORT_EXCEL_DESCRIPTION))
					: null,
			]),
			m('div', { className: CSS.file.drop }, this.fileUpload.view()),
		]);
	}

	viewFileMappings() {
		const collectionCount = this.csvImport.collections.length;

		const tooManyCollections = m(
			'div',
			{ className: CSS.isTooManyCollections },
			Translate(T.COLLECTION_IMPORT_TOO_MANY, {
				count: collectionCount,
				max: MAX_COLLECTIONS,
			})
		);
		return m('div', { className: CSS.csv.container }, [
			m('div', { className: CSS.option.textContainer }, [
				this.viewTickOrNeeded(this.isValid),
				m('span', { className: CSS.option.text }, Translate(T.COLLECTION_IMPORT_MAP_FIELDS)),
			]),

			collectionCount > MAX_COLLECTIONS ? tooManyCollections : null,
			this.csvImport.view(),
		]);
	}
}

function renderAllImportResults(jobResults: JobResult[], skippedFiles: string[] = []): MithrilVNode {
	const isSingleResult = jobResults.length === 1 && skippedFiles.length === 0;
	const failedJobCount = jobResults.filter(
		(job) =>
			job.results.collections.some((c) => c.status === 'error') || job.results.meta.some((n) => n.level === 'error')
	).length;
	const successfulJobCount = jobResults.length - failedJobCount;
	return m('div', { className: CSS.importNotifications.container }, [
		jobResults.length > 0 || skippedFiles.length > 0
			? m('div', { className: CSS.importNotifications.importSummary }, [
					Translate(T.COLLECTION_IMPORT_RESULT_SUMMARY, {
						successful: successfulJobCount,
						failed: failedJobCount,
						skipped: skippedFiles.length,
					}),
				])
			: null,
		skippedFiles.length > 0 ? renderSkippedFilesAccordion(skippedFiles) : null,
		...jobResults.map((job) => {
			const tag = AppStore.Tag.get(job.jobID);
			const jobNameDisplay = tag ? tag.name : job.jobID;
			const successfulCollectionCount = job.results.collections.filter(
				(c) => c.status === 'created' || c.status === 'updated'
			).length;
			const failedCollectionCount = job.results.collections.filter((c) => c.status === 'error').length;
			const hasError =
				job.results.collections.some((c) => c.status === 'error') || job.results.meta.some((n) => n.level === 'error');
			return Accordion.view(
				{
					title: jobNameDisplay,
					subtitle: Translate(T.COLLECTION_IMPORT_JOB_COLLECTION_COUNT, {
						successful: successfulCollectionCount,
						failed: failedCollectionCount,
					}),
					icon: hasError ? Icon.view(MaterialIcon.WARNING) : null,
					open: isSingleResult,
				},
				[
					job.results.meta.length ? renderNotifications(job.results.meta) : null,
					...job.results.collections.map((result) => renderCollectionResult(job.jobID, result)),
				]
			);
		}),
	]);
}

function renderSkippedFilesAccordion(skippedFiles: string[]): MithrilVNode {
	return Accordion.view({ title: 'Skipped Files', icon: Icon.view(MaterialIcon.WARNING) }, [
		m(
			'ul',
			{ className: CSS.importNotifications.skippedFilesList },
			skippedFiles.map((name) => m('li', name))
		),
	]);
}

function renderCollectionResult(jobID: string, result: ImportResult['collections'][number]): MithrilVNode {
	const titleText = result.collection?.createdAt
		? m(
				'a',
				{
					className: CSS.importNotifications.collectionLink,
					href: Services.Route.getRouteURL(IkePageName.map, {
						t: jobID,
						collection: result.collection?.uuid,
					}),
				},
				result.title
			)
		: result.title;
	return m('div', [
		m('div', [importResultIcon(result.status), titleText, `(${translateStatus(result.status)})`]),
		renderNotifications(result.notifications),
	]);
}

function importResultIcon(status: ImportResult['collections'][0]['status']): MithrilVNode {
	switch (status) {
		case 'created':
			return Icon.view(MaterialIcon.ADD);
		case 'updated':
			return Icon.view(MaterialIcon.EDIT);
		case 'error':
			return Icon.view(MaterialIcon.WARNING);
		default:
			return null;
	}
}

function renderNotifications(notifications: ExcelNotification[]): MithrilVNode {
	if (notifications.length === 0) {
		return null;
	}
	return m('table', [
		m('tr', [
			m('th', 'Sheet'),
			m('th', 'Field'),
			m('th', 'Cell'),
			// m('th', 'Severity'),
			m('th', 'Reason'),
		]),
		...notifications.map((n) =>
			m('tr', [
				m('td', n.sheetName),
				m('td', n.fieldName),
				m('td', n.cell),
				// m('td', n.level),
				m(
					'td',
					n.reason === 'import_exception'
						? n.message
						: Translate(`REASON_EXPORT_IMPORTABLE_EXCEL_${n.reason.toUpperCase()}`)
				),
			])
		),
	]);
}

function translateStatus(status: ImportResult['collections'][number]['status']): string {
	switch (status) {
		case 'created':
			return Translate(T.CREATED);
		case 'updated':
			return Translate(T.UPDATED);
		case 'error':
			return Translate(T.IMPORT_FAILED);
		default:
			return '';
	}
}
